/* /org — port of prototype pgOrg (gaps.js). */
import { Link } from "react-router-dom";
import { useAllEmployees, useSettings } from "../../data/hooks";
import type { Employee } from "../../data/types";
import { useI18n, LOOKUP } from "../../i18n";
import { useOverlays } from "../../app/overlays";
import { Avatar, Button, EmptyState, Loaded, PageHeader } from "../../ui";

/** Preferred department heads (prototype); falls back to the highest-paid member of the department. */
export const HEADS: Record<string, number> = { ops: 106, fin: 101, sales: 111, wh: 107, cs: 104 };
export const TEAM_SHOWN = 5;

export function headOf(dept: string, members: Employee[]): Employee | undefined {
  return members.find((e) => e.id === HEADS[dept]) ?? members.slice().sort((a, b) => b.basic - a.basic || a.id - b.id)[0];
}

export default function Org() {
  const i = useI18n();
  const { t, L } = i;
  const { openPrint } = useOverlays();
  const q = useAllEmployees();
  const sq = useSettings();

  const node = (e: Employee, cls = "") => (
    <Link key={e.id} className={"onode " + cls} to={"/employees/" + e.id} data-testid={"org-node-" + e.id}>
      <Avatar name={e.nameEn} size="sm" />
      <span><b>{i.name(e)}</b><small>{i.title(e.title)}</small></span>
    </Link>
  );

  return (
    <Loaded q={q}>
      {(all) => {
        const emps = all.filter((e) => e.status !== "Terminated");
        const depts = Object.keys(LOOKUP.DEPT).filter((k) => emps.some((e) => e.dept === k));
        const tenant = sq.data?.tenant;
        return (
          <div data-testid="org">
            <PageHeader
              title={L("Organization chart", "الهيكل التنظيمي")}
              sub={(tenant ? i.name({ nameEn: tenant.name, nameAr: tenant.nameAr }) : t("Al Waha Trading Est.")) + " · " + depts.length + " " + t("Departments") + " · " + emps.length + " " + L("employees", "موظفًا")}
              actions={<Button icon="printer" onClick={() => openPrint({ doc: "directory" })} data-testid="btn-print">{t("Print")}</Button>}
            />
            <section className="card card-b">
              {!emps.length ? <EmptyState title={L("No employees yet", "لا يوجد موظفون بعد")} desc={L("Add or import employees to build the chart.", "أضف الموظفين أو استوردهم لبناء الهيكل.")} /> : (
                <div className="org">
                  <div className="org-top">
                    <div className="onode root" data-testid="org-root">
                      <span className="avatar t1" aria-hidden="true">AH</span>
                      <span><b>{L("Abdullah Al-Harbi", "عبدالله الحربي")}</b><small>{L("Owner & General Manager", "المالك والمدير العام")}</small></span>
                    </div>
                  </div>
                  <div className="org-row">
                    {depts.map((k) => {
                      const members = emps.filter((e) => e.dept === k);
                      const h = headOf(k, members)!;
                      const team = members.filter((e) => e.id !== h.id);
                      return (
                        <div key={k} className="org-col" data-testid={"org-dept-" + k}>
                          <div className="org-dept">{i.dept(k)} · <span className="num">{members.length}</span></div>
                          {node(h, "head")}
                          <div className="org-team">
                            {team.slice(0, TEAM_SHOWN).map((e) => node(e))}
                            {team.length > TEAM_SHOWN && <Link className="org-more" to="/employees" data-testid={"org-more-" + k}>+{team.length - TEAM_SHOWN} {t("More")}</Link>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>
          </div>
        );
      }}
    </Loaded>
  );
}
